import { readonly, shallowRef } from "vue"

export type NativeCameraStatus =
  | "unavailable"
  | "available"
  | "starting"
  | "streaming"
  | "inactive"
  | "saving"
  | "permission-required"
  | "permission-denied"
  | "error"

export type NativeCameraFlashMode = "off" | "auto" | "on"

export interface NativeCameraZoomState {
  minimumRatio: number
  maximumRatio: number
  ratio: number
  linearZoom: number
}

export function useNativeCamera() {
  const port = window.NinjanizrCamera
  const available = shallowRef(port !== undefined)
  const status = shallowRef<NativeCameraStatus>(port ? "available" : "unavailable")
  const zoomState = shallowRef<NativeCameraZoomState | null>(null)
  let active = false
  let element: HTMLElement | null = null
  let resizeObserver: ResizeObserver | null = null

  function updateViewport(): void {
    if (!port || !element || window.innerWidth <= 0 || window.innerHeight <= 0) return
    const bounds = element.getBoundingClientRect()
    const left = clamp(bounds.left / window.innerWidth)
    const top = clamp(bounds.top / window.innerHeight)
    const width = clamp(bounds.width / window.innerWidth, 0, 1 - left)
    const height = clamp(bounds.height / window.innerHeight, 0, 1 - top)
    if (width === 0 || height === 0) return
    port.postMessage(`viewport:${left},${top},${width},${height}`)
  }

  function handleMessage(event: MessageEvent<string>): void {
    if (event.data.startsWith("zoom-state:")) {
      const values = event.data.slice("zoom-state:".length).split(",").map(Number)
      if (values.length !== 4 || values.some((value) => !Number.isFinite(value))) return
      const [minimumRatio, maximumRatio, ratio, linearZoom] = values as [
        number,
        number,
        number,
        number,
      ]
      if (
        minimumRatio <= 0 ||
        maximumRatio < minimumRatio ||
        ratio < minimumRatio ||
        ratio > maximumRatio ||
        linearZoom < 0 ||
        linearZoom > 1
      ) {
        return
      }
      zoomState.value = { minimumRatio, maximumRatio, ratio, linearZoom }
      return
    }
    if (!event.data.startsWith("state:")) return
    const next = event.data.slice("state:".length)
    if (isNativeCameraStatus(next)) status.value = next
  }

  function startPreview(viewfinder: HTMLElement): void {
    if (!port) return
    resizeObserver?.disconnect()
    element = viewfinder
    active = true
    status.value = "starting"
    // AndroidX injects a message port with an onmessage property, not an EventTarget.
    // oxlint-disable-next-line unicorn/prefer-add-event-listener
    port.onmessage = handleMessage
    document.documentElement.classList.add("ninjanizr-native-camera")
    window.addEventListener("resize", updateViewport)
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(updateViewport)
      resizeObserver.observe(viewfinder)
    }
    updateViewport()
    port.postMessage("preview:start")
  }

  function stopPreview(): void {
    if (!port || !active) return
    active = false
    port.postMessage("preview:stop")
    status.value = "available"
  }

  function focus(x: number, y: number): void {
    if (!port) return
    port.postMessage(`focus:${clamp(x)},${clamp(y)}`)
  }

  function setZoom(value: number): void {
    if (!port) return
    port.postMessage(`zoom:${clamp(value)}`)
  }

  function setZoomRatio(value: number): void {
    if (!port || !Number.isFinite(value) || value <= 0) return
    const state = zoomState.value
    const ratio = state ? clamp(value, state.minimumRatio, state.maximumRatio) : value
    port.postMessage(`zoom-ratio:${ratio}`)
    if (state) zoomState.value = { ...state, ratio }
  }

  function setFlash(mode: NativeCameraFlashMode): void {
    port?.postMessage(`flash:${mode}`)
  }

  function setSystemChrome(dark: boolean): void {
    port?.postMessage(dark ? "chrome:dark" : "chrome:light")
  }

  function requestPermission(): void {
    port?.postMessage("permission:request")
  }

  function openSettings(): void {
    port?.postMessage("settings:open")
  }

  function dispose(): void {
    stopPreview()
    resizeObserver?.disconnect()
    resizeObserver = null
    window.removeEventListener("resize", updateViewport)
    // oxlint-disable-next-line unicorn/prefer-add-event-listener
    if (port?.onmessage === handleMessage) port.onmessage = null
    element = null
    document.documentElement.classList.remove("ninjanizr-native-camera")
  }

  return {
    available: readonly(available),
    status: readonly(status),
    zoomState: readonly(zoomState),
    startPreview,
    stopPreview,
    focus,
    setZoom,
    setZoomRatio,
    setFlash,
    setSystemChrome,
    requestPermission,
    openSettings,
    dispose,
  }
}

function clamp(value: number, minimum = 0, maximum = 1): number {
  return Math.min(maximum, Math.max(minimum, value))
}

function isNativeCameraStatus(value: string): value is NativeCameraStatus {
  return [
    "available",
    "starting",
    "streaming",
    "inactive",
    "saving",
    "permission-required",
    "permission-denied",
    "error",
  ].includes(value)
}
